import { przezmorzeApi, przezmorzeBatchApi, throwMessageOnBadRequestStatus } from "../../api/api";
import type {
  CreateCustomerRequest,
  CustomerBankAccounts,
  CustomerCalculationStatistics,
  CustomerContactPerson,
  CustomerFile,
  CustomerLatLng,
  CustomerListItem,
  CustomerListResponse,
  CustomerOrderStatistics,
  CustomerResponse,
  CustomerStatus,
  DeleteCustomerAttachmentResponse,
  ImportCustomerContactPersonsResponse,
  ImportCustomerNotesResponse,
  ImportCustomersResponse,
  UpdateCustomerRequest,
} from "@przezmorze/api-types";

export type {
  CreateCustomerRequest,
  CustomerBankAccounts,
  CustomerContactPerson,
  CustomerFile,
  CustomerLatLng,
  CustomerListItem,
  CustomerOrderStatistics,
  CustomerResponse,
  CustomerStatus,
  ImportCustomerContactPersonsResponse,
  ImportCustomerNotesResponse,
  ImportCustomersResponse,
  UpdateCustomerRequest,
};

// The backend trims these fields per-role, not per-data: for ADMIN/AGENT the customer mapper always populates
// them, and for CUSTOMER it always omits them — see customers.controller.ts's customerToCustomerListResponse and
// customerToCustomerResponse. Every page under /customers is forbidden for CUSTOMER (App.tsx forbiddenRoutes),
// so a customer arriving there is guaranteed to have them; this narrowing lets the list and edit pages treat them
// as always-present instead of threading `?? []`/`?? {}` fallbacks through every call site. `info`, `invoiceEmail`,
// `taxNo`, `contactDate` and `meetingDate` stay optional here on purpose: they are genuinely optional per-data.
type StaffOnlyListFields = "status" | "files" | "tags" | "contacts" | "travelPay" | "publicContact";

export type AdminCustomerListItem = Omit<CustomerListItem, StaffOnlyListFields> &
  Required<Pick<CustomerListItem, StaffOnlyListFields>>;

type StaffOnlyFields = StaffOnlyListFields | "details" | "bankAccounts";

export type AdminCustomerResponse = Omit<CustomerResponse, StaffOnlyFields> &
  Required<Pick<CustomerResponse, StaffOnlyFields>>;

export async function listCustomers(abortSignal?: AbortSignal): Promise<CustomerListItem[]> {
  const response = await przezmorzeApi.get<CustomerListResponse>("/customers", {
    signal: abortSignal,
  });
  return response.data.items;
}

export async function getCustomer(customerId: string, abortSignal?: AbortSignal): Promise<CustomerResponse | null> {
  if (!customerId) {
    return null;
  }
  const response = await przezmorzeApi.get<CustomerResponse>(`/customers/${customerId}`, {
    signal: abortSignal,
  });
  return response.data;
}

export async function createCustomer(
  request: CreateCustomerRequest,
  abortSignal?: AbortSignal,
): Promise<CustomerResponse> {
  try {
    const response = await przezmorzeApi.post<CustomerResponse>("/customers", request, {
      signal: abortSignal,
    });
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function updateCustomer(
  customerId: string,
  request: UpdateCustomerRequest,
  abortSignal?: AbortSignal,
): Promise<CustomerResponse> {
  try {
    const response = await przezmorzeApi.put<CustomerResponse>(`/customers/${customerId}`, request, {
      signal: abortSignal,
    });
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function deleteCustomer(customerId: string, abortSignal?: AbortSignal): Promise<void> {
  await przezmorzeApi.delete(`/customers/${customerId}`, {
    signal: abortSignal,
  });
}

export async function addCustomerAttachment(
  customerId: string,
  body: FormData,
  abortSignal?: AbortSignal,
): Promise<CustomerFile> {
  const response = await przezmorzeApi.post<CustomerFile>(`/customers/${customerId}/attachments`, body, {
    signal: abortSignal,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
}

export async function deleteCustomerAttachment(
  customerId: string,
  name: string,
  abortSignal?: AbortSignal,
): Promise<CustomerFile[]> {
  const response = await przezmorzeApi.delete<DeleteCustomerAttachmentResponse>(
    `/customers/${customerId}/attachments/${name}`,
    {
      signal: abortSignal,
    },
  );
  return response.data.items;
}

export async function setCustomerLogo(
  customerId: string,
  body: FormData,
  abortSignal?: AbortSignal,
): Promise<CustomerFile> {
  const response = await przezmorzeApi.put<CustomerFile>(`/customers/${customerId}/logo`, body, {
    signal: abortSignal,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
}

export async function deleteCustomerLogo(customerId: string, abortSignal?: AbortSignal): Promise<void> {
  await przezmorzeApi.delete<void>(`/customers/${customerId}/logo`, {
    signal: abortSignal,
  });
}

export async function getCustomerOrdersStatistics(
  customerId: string,
  year: number,
  abortSignal?: AbortSignal,
): Promise<CustomerOrderStatistics> {
  const response = await przezmorzeApi.get<CustomerOrderStatistics>(`/customers-overview/${customerId}/order-stats`, {
    params: {
      year,
    },
    signal: abortSignal,
  });
  return response.data;
}

export async function getCustomersOrdersHistoryCsv(abortSignal?: AbortSignal): Promise<Blob> {
  const response = await przezmorzeApi.get<Blob>("/customers-overview/orders-history", {
    signal: abortSignal,
    responseType: "blob",
  });
  return response.data;
}

export async function getCustomerCalculationsStatistics(
  customerId: string,
  year: number,
  abortSignal?: AbortSignal,
): Promise<CustomerCalculationStatistics> {
  const response = await przezmorzeApi.get<CustomerCalculationStatistics>(
    `/customers-overview/${customerId}/calculation-stats`,
    {
      params: {
        year,
      },
      signal: abortSignal,
    },
  );
  return response.data;
}

export async function importCustomers(body: FormData, abortSignal?: AbortSignal): Promise<ImportCustomersResponse> {
  try {
    const response = await przezmorzeBatchApi.post<ImportCustomersResponse>(`/customer-import/csv`, body, {
      signal: abortSignal,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function importCustomerContactPersons(
  body: FormData,
  abortSignal?: AbortSignal,
): Promise<ImportCustomerContactPersonsResponse> {
  try {
    const response = await przezmorzeBatchApi.post<ImportCustomerContactPersonsResponse>(
      `/customer-import/contact-person-csv`,
      body,
      {
        signal: abortSignal,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}

export async function importCustomerNotes(
  body: FormData,
  abortSignal?: AbortSignal,
): Promise<ImportCustomerNotesResponse> {
  try {
    const response = await przezmorzeBatchApi.post<ImportCustomerNotesResponse>(`/customer-import/notes-csv`, body, {
      signal: abortSignal,
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    throwMessageOnBadRequestStatus(error);
  }
}
